/**
 * @file PlacementNotices.tsx
 * @description Полосы состояния шторки размещения (phase19.24 этап 1, шаг 4;
 * полоса про оставшийся черновик — правка по ревью 2026-09-20).
 *
 * Четыре разных сообщения об одном действии легко превращаются в кашу из
 * условий посреди разметки, поэтому они собраны здесь и идут в одном порядке:
 * сначала «нечем работать» (ячейки не загрузились или их нет), потом отказ
 * сервера, потом судьба документа, потом объяснение пропусков.
 *
 * Отдельная полоса про черновик нужна потому, что у проведения нет
 * атомарности: упав на строке, сервер оставляет документ MV черновиком. Без
 * ссылки кладовщик о нём не узнает и заведёт второй.
 */

import { Link } from "react-router";

import { PLACEMENT_TEXT } from "./stock-placement-texts";
import type { PlacementError } from "./stock-placement-error";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";

interface Props {
  /** Справочник ячеек не загрузился — работать нечем. */
  binsError: string | null;
  /** На складе ячеек нет вовсе. */
  noBins: boolean;
  /** Отказ проведения; `null` — попытки не было или она прошла. */
  error: PlacementError | null;
  /** Адрес карточки перемещения по id — ссылка на оставшийся черновик. */
  transferHref: (transferId: string) => string;
  /** Партии, уже лежавшие в выбранной ячейке; `null` — таких не было. */
  skippedNotice: string | null;
}

export function PlacementNotices({
  binsError,
  noBins,
  error,
  transferHref,
  skippedNotice,
}: Props) {
  return (
    <>
      {binsError ? <Alert severity="error">{binsError}</Alert> : null}
      {noBins ? <NoticeStrip tone="warning">{PLACEMENT_TEXT.noBins}</NoticeStrip> : null}
      {error ? <NoticeStrip tone="error">{error.message}</NoticeStrip> : null}

      {/* Документ остался черновиком: полоса уводит прямо в него — там
          «Обновить строки» приводит состав к текущим остаткам. */}
      {error?.partialNotice && error.transferId ? (
        <NoticeStrip
          tone="warning"
          action={
            <Button variant="outline" className="min-h-12" asChild>
              <Link to={transferHref(error.transferId)}>
                {PLACEMENT_TEXT.openDocument}
              </Link>
            </Button>
          }
        >
          {error.partialNotice}
        </NoticeStrip>
      ) : null}

      {/* Пропущенные партии — не ошибка, а объяснение: часть строк уже
          лежала в выбранной ячейке, документа на них нет. */}
      {skippedNotice ? <NoticeStrip tone="info">{skippedNotice}</NoticeStrip> : null}
    </>
  );
}
